import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useNavigate, useRevalidator } from "react-router";
import { ArrowLeft, BarChart3, Pause, Play, Rocket, Save, Square, Trash2 } from "lucide-react";
import { toast } from "@/components/feedback/toast";
import { newSurveyInput } from "@/lib/surveys/defaults";
import { publishProblems } from "@/lib/surveys/validate";
import { listTexts } from "@/lib/surveys/translations";
import type { Lang, SurveyInput, SurveyRecord, SurveyStatus } from "@/lib/surveys/types";
import { QuestionEditor } from "./question-editor";
import { AudienceEditor } from "./audience-editor";
import { Card, Field, inputCls, StatusChip, Toggle } from "./form";
import { adminFetch } from "@/api";

type Tab = "basics" | "questions" | "audience" | "schedule" | "translations" | "test";
const TABS: { id: Tab; label: string }[] = [
  { id: "basics", label: "Basics" },
  { id: "questions", label: "Questions" },
  { id: "audience", label: "Audience" },
  { id: "schedule", label: "Schedule & frequency" },
  { id: "translations", label: "Translations" },
  { id: "test", label: "Test" },
];

function toInput(r: SurveyRecord): SurveyInput {
  const { title, content, questions, audience, startAt, endAt, responseLimit, maxShows, showCooldownHours, maxDismissals, testEmails } = r;
  return { title, content, questions, audience, startAt, endAt, responseLimit, maxShows, showCooldownHours, maxDismissals, testEmails };
}

/** ISO → value for <input type="datetime-local"> in the browser's zone. */
function isoToLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const localToIso = (v: string) => (v ? new Date(v).toISOString() : null);

interface Props {
  survey: SurveyRecord | null; // null = new
  canEdit: boolean;
}

export function SurveyEditor({ survey, canEdit }: Props) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [input, setInput] = useState<SurveyInput>(() => (survey ? toInput(survey) : newSurveyInput("Untitled survey")));
  const [saved, setSaved] = useState(() => JSON.stringify(survey ? toInput(survey) : null));
  const [status, setStatus] = useState<SurveyStatus>(survey?.status ?? "draft");
  const [tab, setTab] = useState<Tab>("basics");
  const [lang, setLang] = useState<Lang>("en");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [emailsText, setEmailsText] = useState(input.testEmails.join(", "));

  const id = survey?.id;
  const readOnly = !canEdit || status === "ended";
  const locked = status !== "draft";
  const dirty = JSON.stringify(input) !== saved;
  const problems = useMemo(() => publishProblems(input), [input]);
  const missingHindi = useMemo(() => listTexts(input).some((t) => t.value.en.trim() && !t.value.hi.trim()), [input]);

  const patchContent = (fn: (c: SurveyInput["content"]) => SurveyInput["content"]) =>
    setInput((s) => ({ ...s, content: fn(s.content) }));

  async function call(url: string, method: string, body?: unknown) {
    const res = await adminFetch(url, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    return { res, json };
  }

  function reportError(json: { error?: string; details?: { errors?: string[]; problems?: string[] } }) {
    const list = json.details?.errors ?? json.details?.problems ?? [];
    setErrors(list);
    const msg: Record<string, string> = {
      SURVEY_LOCKED: "Questions are locked on a live survey — only wording can change.",
      SURVEY_NOT_READY: "Not ready to publish yet — see the list above.",
      SURVEY_ENDED: "This survey has ended and can't be edited.",
      FORBIDDEN: "Your role can't change surveys.",
      VALIDATION_ERROR: "Please fix the highlighted problems.",
    };
    toast.error(msg[json.error ?? ""] ?? json.error ?? "Something went wrong");
  }

  async function save(): Promise<string | null> {
    const body = { ...input, testEmails: parseEmails(emailsText) };
    setBusy(true);
    try {
      const { res, json } = id ? await call(`/api/surveys/${id}`, "PUT", body) : await call("/api/surveys", "POST", body);
      if (!res.ok) {
        reportError(json);
        return null;
      }
      const rec = json.data as SurveyRecord;
      setErrors([]);
      setInput(toInput(rec));
      setSaved(JSON.stringify(toInput(rec)));
      setEmailsText(rec.testEmails.join(", "));
      toast.success("Saved");
      if (!id) navigate(`/admin/surveys/${rec.id}`, { replace: true });
      return rec.id;
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (problems.length) {
      setErrors(problems);
      toast.error("Not ready to publish yet — see the list above.");
      return;
    }
    if (!window.confirm("Publish this survey? Matching users will start seeing it, and its questions will be locked.")) return;
    const sid = dirty || !id ? await save() : id;
    if (!sid) return;
    setBusy(true);
    try {
      const { res, json } = await call(`/api/surveys/${sid}/publish`, "POST");
      if (!res.ok) return reportError(json);
      setStatus("active");
      toast.success("Published — it's live");
      void revalidate();
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(target: "paused" | "active" | "ended") {
    const q = {
      paused: "Pause this survey? Nobody new will see it until you resume.",
      active: "Resume this survey?",
      ended: "End this survey for good? This can't be undone.",
    }[target];
    if (!window.confirm(q)) return;
    setBusy(true);
    try {
      const { res, json } = await call(`/api/surveys/${id}/status`, "POST", { status: target });
      if (!res.ok) return reportError(json);
      setStatus(target);
      toast.success(target === "paused" ? "Paused" : target === "active" ? "Resumed" : "Ended");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this draft?")) return;
    const { res, json } = await call(`/api/surveys/${id}`, "DELETE");
    if (!res.ok) return reportError(json);
    toast.success("Draft deleted");
    navigate("/admin/surveys");
  }

  async function resetTests() {
    if (!window.confirm("Delete the test users' answers so they see the survey again?")) return;
    const { res, json } = await call(`/api/surveys/${id}/reset-tests`, "POST");
    if (!res.ok) return reportError(json);
    toast.success(`Reset ${json.data.deleted} test response(s)`);
  }

  const btn = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12.5px] font-semibold border transition-all disabled:opacity-40";
  const ghost = `${btn} bg-white/[0.04] border-white/[0.06] text-zinc-200 hover:text-white`;

  return (
    <div className="space-y-5">
      <Link to="/admin/surveys" className="inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300">
        <ArrowLeft className="w-3.5 h-3.5" /> Surveys
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-bold text-white tracking-tight">{input.title || "Untitled survey"}</h1>
        <StatusChip status={status} />
        {dirty && !readOnly && <span className="text-[11.5px] text-amber-400">Unsaved changes</span>}
        <div className="flex-1" />
        {id && status !== "draft" && (
          <Link to={`/admin/surveys/${id}/report`} className={ghost}><BarChart3 className="w-3.5 h-3.5" /> View report</Link>
        )}
        {canEdit && status === "draft" && id && (
          <button type="button" disabled={busy} onClick={remove} className={`${btn} bg-transparent border-white/[0.06] text-red-400`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        )}
        {canEdit && status === "active" && (
          <button type="button" disabled={busy} onClick={() => changeStatus("paused")} className={`${btn} bg-transparent border-white/[0.06] text-amber-400`}><Pause className="w-3.5 h-3.5" /> Pause</button>
        )}
        {canEdit && status === "paused" && (
          <button type="button" disabled={busy} onClick={() => changeStatus("active")} className={`${btn} bg-transparent border-white/[0.06] text-emerald-400`}><Play className="w-3.5 h-3.5" /> Resume</button>
        )}
        {canEdit && (status === "active" || status === "paused") && (
          <button type="button" disabled={busy} onClick={() => changeStatus("ended")} className={`${btn} bg-transparent border-white/[0.06] text-red-400`}><Square className="w-3.5 h-3.5" /> End</button>
        )}
        {!readOnly && (
          <button type="button" disabled={busy || (!dirty && !!id)} onClick={save} className={ghost}><Save className="w-3.5 h-3.5" /> Save</button>
        )}
        {canEdit && status === "draft" && (
          <button type="button" disabled={busy} onClick={publish} className={`${btn} bg-emerald-500 border-emerald-500 text-black`}><Rocket className="w-3.5 h-3.5" /> Publish</button>
        )}
      </div>

      {!canEdit && <p className="text-[12px] text-zinc-500">You have view-only access.</p>}

      {errors.length > 0 && (
        <div className="rounded-xl border border-red-500/25 bg-red-500/[0.06] px-4 py-3 text-[12.5px] text-red-300 space-y-0.5">
          {errors.map((e) => <div key={e}>• {e}</div>)}
        </div>
      )}

      <div className="flex gap-1 border-b border-white/[0.06] overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3.5 py-2.5 text-[13px] -mb-px border-b-2 whitespace-nowrap ${tab === t.id ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            {t.label}
            {t.id === "translations" && missingHindi && <span className="ml-1 text-amber-400">●</span>}
          </button>
        ))}
        <div className="flex-1" />
        {(tab === "basics" || tab === "questions") && <LangSwitch lang={lang} onChange={setLang} />}
      </div>

      {tab === "basics" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Survey">
            <Field label="Internal name" hint="Only admins see this.">
              <input className={inputCls} disabled={readOnly} value={input.title} onChange={(e) => setInput({ ...input, title: e.target.value })} />
            </Field>
            <Field label="Where it shows">
              <input className={inputCls} disabled value="Home · bottom sheet" readOnly />
            </Field>
            <Field label="Languages">
              <input className={inputCls} disabled value="English + Hindi (both required to publish)" readOnly />
            </Field>
          </Card>
          <Card title={`What users see (${lang.toUpperCase()})`}>
            <Field label="Intro title">
              <input className={inputCls} disabled={readOnly} value={input.content.intro.title[lang]} onChange={(e) => patchContent((c) => ({ ...c, intro: { ...c.intro, title: { ...c.intro.title, [lang]: e.target.value } } }))} />
            </Field>
            <Field label="Intro text">
              <textarea rows={2} className={`${inputCls} resize-none`} disabled={readOnly} value={input.content.intro.body[lang]} onChange={(e) => patchContent((c) => ({ ...c, intro: { ...c.intro, body: { ...c.intro.body, [lang]: e.target.value } } }))} />
            </Field>
            <Field label="Thank-you title">
              <input className={inputCls} disabled={readOnly} value={input.content.thankYou.title[lang]} onChange={(e) => patchContent((c) => ({ ...c, thankYou: { ...c.thankYou, title: { ...c.thankYou.title, [lang]: e.target.value } } }))} />
            </Field>
            <Field label="Thank-you text">
              <textarea rows={2} className={`${inputCls} resize-none`} disabled={readOnly} value={input.content.thankYou.body[lang]} onChange={(e) => patchContent((c) => ({ ...c, thankYou: { ...c.thankYou, body: { ...c.thankYou.body, [lang]: e.target.value } } }))} />
            </Field>
            <Toggle label="Feedback box at the end (optional for users)" checked={input.content.feedback.enabled} disabled={readOnly} onChange={(v) => patchContent((c) => ({ ...c, feedback: { ...c.feedback, enabled: v } }))} />
            {input.content.feedback.enabled && (
              <div className="grid grid-cols-[1fr_120px] gap-3">
                <Field label="Feedback prompt">
                  <input className={inputCls} disabled={readOnly} value={input.content.feedback.prompt[lang]} onChange={(e) => patchContent((c) => ({ ...c, feedback: { ...c.feedback, prompt: { ...c.feedback.prompt, [lang]: e.target.value } } }))} />
                </Field>
                <Field label="Max characters">
                  <input type="number" min={1} max={1000} className={inputCls} disabled={readOnly} value={input.content.feedback.maxChars} onChange={(e) => patchContent((c) => ({ ...c, feedback: { ...c.feedback, maxChars: Number(e.target.value) || 1 } }))} />
                </Field>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "questions" && (
        <QuestionEditor questions={input.questions} lang={lang} locked={locked} readOnly={readOnly} onChange={(questions) => setInput({ ...input, questions })} />
      )}

      {tab === "audience" && (
        <AudienceEditor
          audience={input.audience}
          readOnly={readOnly}
          surveyId={id}
          cohortFrozenAt={survey?.cohortFrozenAt ?? null}
          onChange={(audience) => setInput({ ...input, audience })}
        />
      )}

      {tab === "schedule" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Run window">
            <Field label="Starts" hint="Empty = as soon as it's published. Times are in your browser's time zone.">
              <input type="datetime-local" className={inputCls} disabled={readOnly} value={isoToLocal(input.startAt)} onChange={(e) => setInput({ ...input, startAt: localToIso(e.target.value) })} />
            </Field>
            <Field label="Ends" hint="Empty = runs until you end it.">
              <input type="datetime-local" className={inputCls} disabled={readOnly} value={isoToLocal(input.endAt)} onChange={(e) => setInput({ ...input, endAt: localToIso(e.target.value) })} />
            </Field>
            <Field label="Stop after (completed responses)" hint="Ends the survey automatically. Empty = no limit.">
              <input type="number" min={1} className={inputCls} disabled={readOnly} value={input.responseLimit ?? ""} placeholder="no limit" onChange={(e) => setInput({ ...input, responseLimit: e.target.value ? Math.floor(Number(e.target.value)) : null })} />
            </Field>
          </Card>
          <Card title="How often a user sees it">
            <Field label="Show at most (times)">
              <input type="number" min={1} max={20} className={inputCls} disabled={readOnly} value={input.maxShows} onChange={(e) => setInput({ ...input, maxShows: Math.floor(Number(e.target.value)) || 1 })} />
            </Field>
            <Field label="Wait between shows (hours)">
              <input type="number" min={0} className={inputCls} disabled={readOnly} value={input.showCooldownHours} onChange={(e) => setInput({ ...input, showCooldownHours: Math.max(0, Math.floor(Number(e.target.value))) })} />
            </Field>
            <Field label='Stop after "Maybe later" (times)'>
              <input type="number" min={1} max={20} className={inputCls} disabled={readOnly} value={input.maxDismissals} onChange={(e) => setInput({ ...input, maxDismissals: Math.floor(Number(e.target.value)) || 1 })} />
            </Field>
            <p className="text-[11.5px] text-zinc-500">Never shown again once completed. A half-done survey comes back so the user can finish it.</p>
          </Card>
        </div>
      )}

      {tab === "translations" && (
        <Card title="English → Hindi">
          {problems.length > 0 && (
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-2.5 text-[12px] text-amber-300">
              {problems.length} thing(s) still block publishing. Every text needs both English and Hindi.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="py-2 pr-3 w-36" />
                  <th className="py-2 pr-3">English</th>
                  <th className="py-2">Hindi</th>
                </tr>
              </thead>
              <tbody>
                {listTexts(input).map((t) => (
                  <tr key={t.label} className="border-t border-white/[0.06] align-top">
                    <td className="py-2 pr-3 text-zinc-500 text-[11.5px]">{t.label}</td>
                    {(["en", "hi"] as const).map((l) => (
                      <td key={l} className="py-2 pr-3">
                        <input
                          aria-label={`${t.label} ${l === "en" ? "English" : "Hindi"}`}
                          className={`${inputCls} ${!t.value[l].trim() ? "border-amber-500/40" : ""}`}
                          disabled={readOnly}
                          value={t.value[l]}
                          placeholder="Missing"
                          onChange={(e) => setInput((s) => t.apply(s, { ...t.value, [l]: e.target.value }))}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "test" && (
        <Card title="Test users">
          <p className="text-[12.5px] text-zinc-400">
            These people always see the survey — even as a draft, while paused, or outside the audience — until they complete it. Try it on your own phone before publishing.
          </p>
          <Field label="Emails" hint="Separate with commas or new lines. Saved with the survey.">
            <textarea rows={3} className={`${inputCls} resize-none`} disabled={readOnly} value={emailsText} onChange={(e) => {
              setEmailsText(e.target.value);
              setInput((s) => ({ ...s, testEmails: parseEmails(e.target.value) }));
            }} />
          </Field>
          {canEdit && id && (
            <button type="button" onClick={resetTests} className={ghost}>Reset test responses</button>
          )}
        </Card>
      )}
    </div>
  );
}

function parseEmails(text: string): string[] {
  return Array.from(new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)));
}

function LangSwitch({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <div className="self-center inline-flex rounded-lg border border-white/[0.06] bg-white/[0.02] p-0.5 mb-1">
      {(["en", "hi"] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onChange(l)}
          className={`px-3 py-1 rounded-md text-[12px] font-semibold ${lang === l ? "bg-white/[0.1] text-white" : "text-zinc-500"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
